import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import Sidebar from '../components/Sidebar';
import { useAuth } from '../context/AuthContext';
import { CheckCircle, Circle, ArrowRight, ArrowLeft, User, FileText, Shield, Briefcase, Rocket, Upload, Star } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import * as api from '../lib/api';

const OnboardingPage = () => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  useEffect(() => {
    const load = async () => {
      try {
        const data = await api.getOnboardingProgress();
        if (data.completed_steps) setCompletedSteps(new Set(data.completed_steps));
        if (data.current_step) setCurrentStep(data.current_step);
      } catch {}
    };
    load();
  }, [user]);

  const steps = [
    { id: 0, title: 'Welcome', icon: Rocket, content: 'welcome' },
    { id: 1, title: 'Profile Setup', icon: User, content: 'profile' },
    { id: 2, title: 'Skills Assessment', icon: Star, content: 'skills' },
    { id: 3, title: 'Verification', icon: Shield, content: 'verification' },
    { id: 4, title: 'Tax Information', icon: FileText, content: 'tax' },
    { id: 5, title: 'Ready to Work', icon: Briefcase, content: 'complete' },
  ];

  const handleCompleteStep = async () => {
    const newCompleted = new Set(completedSteps);
    newCompleted.add(currentStep);
    setCompletedSteps(newCompleted);
    try { await api.updateOnboardingStep(currentStep); } catch {}
    if (currentStep < steps.length - 1) setCurrentStep(currentStep + 1);
    toast({ title: 'Step completed!', description: `${steps[currentStep].title} has been completed.` });
  };

  const progress = Math.round((completedSteps.size / steps.length) * 100);

  const renderContent = () => {
    switch (steps[currentStep].content) {
      case 'welcome': return (
        <div className="text-center py-8">
          <div className="w-24 h-24 bg-[#082900] rounded-2xl flex items-center justify-center mx-auto mb-6"><Rocket className="w-12 h-12 text-white" /></div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Welcome to Duty AI!</h2>
          <p className="text-lg text-gray-600 max-w-lg mx-auto mb-6">Complete these steps to set up your profile and start finding amazing AI opportunities.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl mx-auto">
            <Card className="p-4 text-center"><div className="text-2xl font-bold text-[#0A3900] mb-1">78.5k</div><div className="text-sm text-gray-600">Active Roles</div></Card>
            <Card className="p-4 text-center"><div className="text-2xl font-bold text-[#0A3900] mb-1">$78</div><div className="text-sm text-gray-600">Avg. Hourly Pay</div></Card>
            <Card className="p-4 text-center"><div className="text-2xl font-bold text-[#0A3900] mb-1">$1.5M+</div><div className="text-sm text-gray-600">Daily Payouts</div></Card>
          </div>
        </div>
      );
      case 'profile': return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Complete Your Profile</h2>
          <p className="text-gray-600">A complete profile increases your chances of getting matched.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div><Label>Professional Title *</Label><Input className="mt-2" placeholder="e.g., Senior ML Engineer" /></div>
            <div><Label>Years of Experience *</Label><select className="w-full mt-2 px-3 py-2 border border-gray-300 rounded-md"><option>0-2 years</option><option>2-5 years</option><option>5-10 years</option><option>10+ years</option></select></div>
          </div>
          <div><Label>Bio *</Label><Textarea className="mt-2" rows={4} placeholder="Tell companies about yourself..." /></div>
          <div><Label>Resume</Label><div className="mt-2 border-2 border-dashed border-gray-300 rounded-lg p-8 text-center"><Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" /><p className="text-sm text-gray-600">Drag & drop your resume or <span className="text-[#0A3900] cursor-pointer">browse</span></p></div></div>
          <div><Label>Portfolio / GitHub URL</Label><Input className="mt-2" placeholder="https://github.com/yourname" /></div>
        </div>
      );
      case 'skills': return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Skills Assessment</h2>
          <p className="text-gray-600">Select your top skills and take optional assessments.</p>
          <div><Label className="mb-3 block">Select your skills (up to 10)</Label><div className="flex flex-wrap gap-2">
            {['Python', 'JavaScript', 'TypeScript', 'React', 'Node.js', 'Machine Learning', 'Deep Learning', 'TensorFlow', 'PyTorch', 'AWS', 'GCP', 'Docker', 'Kubernetes', 'SQL', 'MongoDB', 'Go', 'Rust', 'Java', 'System Design', 'NLP'].map(s => (
              <button key={s} className="px-4 py-2 rounded-full border-2 border-gray-300 text-sm font-medium text-gray-700 hover:border-[#0A3900] hover:text-[#0A3900] transition-colors focus:border-[#0A3900] focus:bg-[#F1F5E6]">{s}</button>
            ))}
          </div></div>
          <Card className="p-6 bg-[#F1F5E6] border-[#B8C4AD]">
            <h3 className="font-semibold text-gray-900 mb-2">Take a Skills Assessment</h3>
            <p className="text-sm text-gray-600 mb-4">Verified assessments boost your profile visibility by 3x.</p>
            <div className="flex gap-3"><Button variant="outline" className="border-[#B8C4AD]">Python (20 min)</Button><Button variant="outline" className="border-[#B8C4AD]">ML (30 min)</Button><Button variant="outline" className="border-[#B8C4AD]">System Design (45 min)</Button></div>
          </Card>
          <div><Label>Hourly Rate Expectation (USD)</Label><Input className="mt-2" type="number" placeholder="e.g., 100" /></div>
        </div>
      );
      case 'verification': return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Identity Verification</h2>
          <p className="text-gray-600">Verify your identity to access all features.</p>
          <div className="space-y-4">
            {[{ title: 'Government ID', desc: 'Passport or driver\'s license', required: true }, { title: 'Proof of Address', desc: 'Utility bill or bank statement', required: true }, { title: 'Background Check', desc: 'Consent to background verification', required: false }, { title: 'LinkedIn Verification', desc: 'Connect your LinkedIn profile', required: false }].map((item, idx) => (
              <Card key={idx} className="p-5"><div className="flex items-center justify-between">
                <div className="flex items-center gap-3"><Circle className="w-6 h-6 text-gray-300" /><div><h3 className="font-semibold text-gray-900">{item.title}</h3><p className="text-sm text-gray-600">{item.desc}</p></div></div>
                <div className="flex items-center gap-2">{item.required && <Badge className="bg-red-100 text-red-800">Required</Badge>}<Button variant="outline" size="sm"><Upload className="w-4 h-4 mr-2" />Upload</Button></div>
              </div></Card>
            ))}
          </div>
        </div>
      );
      case 'tax': return (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">Payment & Tax Setup</h2>
          <p className="text-gray-600">Set up your payment method and tax information.</p>
          <Card className="p-6"><h3 className="font-semibold text-gray-900 mb-4">Payment Method</h3>
            <div className="space-y-4"><div><Label>Bank Account (ACH)</Label><div className="grid grid-cols-2 gap-4 mt-2"><Input placeholder="Routing Number" /><Input placeholder="Account Number" /></div></div>
            <p className="text-sm text-gray-500">Or connect via Stripe for instant payouts</p><Button variant="outline">Connect with Stripe</Button></div>
          </Card>
          <Card className="p-6"><h3 className="font-semibold text-gray-900 mb-4">Tax Information</h3>
            <p className="text-gray-600 mb-4">Complete your W-9 form.</p><Button variant="outline" onClick={() => window.location.href = '/taxes'}><FileText className="w-4 h-4 mr-2" />Complete W-9 Form</Button>
          </Card>
        </div>
      );
      case 'complete': return (
        <div className="text-center py-8">
          <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-12 h-12 text-green-600" /></div>
          <h2 className="text-3xl font-bold text-gray-900 mb-4">You're All Set!</h2>
          <p className="text-lg text-gray-600 max-w-lg mx-auto mb-8">Your onboarding is complete. Start exploring jobs and receiving offers.</p>
          <div className="flex justify-center gap-4">
            <Button className="bg-[#0A3900] hover:bg-[#1A5C0A] px-8 py-3" onClick={() => window.location.href = '/explore'}><Briefcase className="w-4 h-4 mr-2" />Explore Jobs</Button>
            <Button variant="outline" className="px-8 py-3" onClick={() => window.location.href = '/profile'}><User className="w-4 h-4 mr-2" />View Profile</Button>
          </div>
        </div>
      );
      default: return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F9FBF5]">
      <Sidebar />
      <div className="flex-1 ml-0 md:ml-[70px] pt-[60px] md:pt-0 pb-[60px] md:pb-0">
        <div className="max-w-5xl mx-auto px-8 py-12">
          <div className="mb-8"><h1 className="text-3xl font-bold text-gray-900 mb-2">Onboarding</h1><p className="text-gray-600">Complete these steps to get started on Duty AI</p></div>

          <Card className="p-6 mb-8">
            <div className="flex items-center justify-between mb-3"><span className="text-sm font-medium text-gray-900">Onboarding Progress</span><span className="text-sm font-medium text-[#0A3900]">{progress}% complete</span></div>
            <div className="w-full bg-gray-200 rounded-full h-3"><div className="bg-[#0A3900] h-3 rounded-full transition-all duration-500" style={{ width: `${progress}%` }} /></div>
          </Card>

          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2">
            {steps.map((step, idx) => {
              const Icon = step.icon;
              const isCompleted = completedSteps.has(idx);
              const isCurrent = currentStep === idx;
              return (
                <button key={idx} onClick={() => setCurrentStep(idx)} className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${isCurrent ? 'bg-[#0A3900] text-white' : isCompleted ? 'bg-green-100 text-green-800' : 'bg-white text-gray-700 border hover:bg-gray-50'}`}>
                  {isCompleted ? <CheckCircle className="w-4 h-4" /> : <Icon className="w-4 h-4" />}{step.title}
                </button>
              );
            })}
          </div>

          <Card className="p-8 mb-8">{renderContent()}</Card>

          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setCurrentStep(Math.max(0, currentStep - 1))} disabled={currentStep === 0}><ArrowLeft className="w-4 h-4 mr-2" />Previous</Button>
            <Button onClick={handleCompleteStep} className="bg-[#0A3900] hover:bg-[#1A5C0A]">{currentStep === steps.length - 1 ? 'Complete Onboarding' : 'Complete & Continue'}<ArrowRight className="w-4 h-4 ml-2" /></Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
